import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, Users, CalendarClock, Radar, Loader2 } from "lucide-react";
import { api, API } from "../lib/api";
import { BRAND } from "../lib/content";
import { ArticlesManager } from "../components/ArticlesManager";

const StatCard = ({ icon: Icon, value, label }) => (
  <div className="border border-white/10 bg-surface p-6">
    <div className="flex items-center justify-between">
      <span className="text-flame"><Icon size={20} /></span>
    </div>
    <div className="mt-4 font-stat text-4xl font-bold text-white">{value}</div>
    <div className="mt-1 font-body text-xs uppercase tracking-[0.2em] text-white/40">{label}</div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ total: 0, last_7_days: 0, sources: 0 });
  const [exporting, setExporting] = useState(false);

  const logout = () => {
    localStorage.removeItem("ta_admin_token");
    navigate("/admin/login");
  };

  useEffect(() => {
    const load = async () => {
      if (!localStorage.getItem("ta_admin_token")) {
        navigate("/admin/login");
        return;
      }
      try {
        const [subsRes, statsRes] = await Promise.all([
          api.get("/admin/subscribers"),
          api.get("/admin/stats"),
        ]);
        setSubs(subsRes.data.subscribers);
        setStats(statsRes.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem("ta_admin_token");
          navigate("/admin/login");
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("ta_admin_token");
      const res = await fetch(`${API}/admin/subscribers/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tattooed-abroad-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="animate-spin text-flame" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink">
      <header className="sticky top-0 z-10 border-b border-white/10 bg-ink/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src={BRAND.initials} alt="Tattooed Abroad" className="h-9 w-auto" />
            <div>
              <p className="font-gothic text-xs uppercase tracking-[0.25em] text-flame">Admin</p>
              <p className="font-display text-lg text-white">Subscribers</p>
            </div>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 border border-white/15 px-5 py-2.5 font-body text-xs uppercase tracking-[0.2em] text-white/70 transition-colors hover:border-flame hover:text-flame"
          >
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard icon={Users} value={stats.total} label="Total Subscribers" />
          <StatCard icon={CalendarClock} value={stats.last_7_days} label="Last 7 Days" />
          <StatCard icon={Radar} value={stats.sources} label="Capture Sources" />
        </div>

        <div className="mt-10 flex items-center justify-between">
          <h2 className="font-display text-2xl font-light text-white">The List</h2>
          <button
            data-testid="admin-export-csv"
            onClick={exportCsv}
            disabled={exporting || subs.length === 0}
            className="inline-flex items-center gap-2 border border-flame bg-flame px-6 py-3 font-body text-xs uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ember disabled:opacity-50"
          >
            {exporting ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />}
            Export CSV
          </button>
        </div>

        <div className="mt-6 overflow-hidden border border-white/10">
          <table className="w-full text-left" data-testid="subscribers-table">
            <thead>
              <tr className="border-b border-white/10 bg-surface">
                <th className="px-6 py-4 font-gothic text-xs uppercase tracking-[0.2em] text-white/50">Email</th>
                <th className="px-6 py-4 font-gothic text-xs uppercase tracking-[0.2em] text-white/50">Source</th>
                <th className="px-6 py-4 font-gothic text-xs uppercase tracking-[0.2em] text-white/50">Joined</th>
              </tr>
            </thead>
            <tbody>
              {subs.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center font-body text-sm text-white/40">
                    No subscribers yet.
                  </td>
                </tr>
              ) : (
                subs.map((s, i) => (
                  <tr key={s.id || i} data-testid="subscriber-row" className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-6 py-4 font-body text-sm text-white">{s.email}</td>
                    <td className="px-6 py-4">
                      <span className="border border-flame/30 bg-flame/10 px-2.5 py-1 font-body text-xs uppercase tracking-wider text-flame">{s.source}</span>
                    </td>
                    <td className="px-6 py-4 font-body text-sm text-white/50">
                      {new Date(s.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <ArticlesManager />
      </main>
    </div>
  );
}
